// Start/end controls for a route — identical capability to the Route Planner:
// type an address (Google autocomplete) or use current GPS, and choose round-trip
// or a different end point. Persists changes to the route as they're set.
import React, { useState } from 'react';
import styled from 'styled-components';
import { FaLocationArrow, FaFlagCheckered, FaPlay, FaSpinner } from 'react-icons/fa';
import GooglePlaceAutocomplete from '../../../components/shared/GooglePlaceAutocomplete';

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`;

const FieldRow = styled.div`
  display: flex; gap: 8px; align-items: flex-start;
  > div:first-child { flex: 1; min-width: 0; }
`;

const GpsBtn = styled.button`
  flex-shrink: 0; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
  height: 48px; padding: 0 13px; border-radius: 10px; cursor: pointer; font-size: 0.8rem; font-weight: 600;
  border: 1px solid ${p => p.theme.colors.primary}; background: ${p => p.theme.colors.primary}1a; color: ${p => p.theme.colors.primary};
  &:hover { background: ${p => p.theme.colors.primary}2e; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const Label = styled.div`
  display: flex; align-items: center; gap: 7px; font-size: 0.74rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px; color: ${p => p.theme.colors.textSecondary};
  svg { color: ${p => p.theme.colors.primary}; }
`;

const Toggle = styled.label`
  display: inline-flex; align-items: center; gap: 9px; cursor: pointer; user-select: none;
  color: ${p => p.theme.colors.textPrimary}; font-size: 0.88rem; padding: 4px 0;
  input { width: 18px; height: 18px; accent-color: ${p => p.theme.colors.primary}; }
`;

const DepartureDestination = ({ departure, destination, isRoundTrip, onChange }) => {
  const [locating, setLocating] = useState(null); // 'departure' | 'destination' | null

  const requestGps = (which) => {
    if (!navigator.geolocation) return;
    setLocating(which);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const point = { label: 'My current location', lat: pos.coords.latitude, lng: pos.coords.longitude };
        if (which === 'departure') onChange({ departure: point });
        else onChange({ destination: point });
        setLocating(null);
      },
      () => setLocating(null),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <Wrap>
      <Label><FaPlay size={11} /> Start</Label>
      <FieldRow>
        <GooglePlaceAutocomplete
          initialValue={departure?.label || ''}
          placeholder="Start address"
          onSelect={(address, lat, lng) => onChange({ departure: { label: address, lat, lng } })}
        />
        <GpsBtn type="button" disabled={locating === 'departure'} onClick={() => requestGps('departure')}>
          {locating === 'departure' ? <FaSpinner className="spin" /> : <FaLocationArrow />} GPS
        </GpsBtn>
      </FieldRow>

      <Toggle>
        <input type="checkbox" checked={isRoundTrip} onChange={(e) => onChange({ isRoundTrip: e.target.checked })} />
        Round trip (return to start)
      </Toggle>

      {!isRoundTrip && (
        <>
          <Label><FaFlagCheckered size={11} /> End</Label>
          <FieldRow>
            <GooglePlaceAutocomplete
              initialValue={destination?.label || ''}
              placeholder="End address"
              onSelect={(address, lat, lng) => onChange({ destination: { label: address, lat, lng } })}
            />
            <GpsBtn type="button" disabled={locating === 'destination'} onClick={() => requestGps('destination')}>
              {locating === 'destination' ? <FaSpinner /> : <FaLocationArrow />} GPS
            </GpsBtn>
          </FieldRow>
        </>
      )}
    </Wrap>
  );
};

export default DepartureDestination;
